/*!
==========================================================
DIGNITY GOLD
TEAM STANGE
Production JS

Part 1
- Shared lifecycle
- Reveal system
- Fullscreen cinematic intro
- Intro sound controls
- Intro progress
- Fullscreen → hero transition
- Reduced motion
- Shopify Theme Editor support
==========================================================
*/

(() => {
  "use strict";

  const HERO_SELECTOR = "[data-dg-team-stange-hero]";
  const REVEAL_SELECTOR = "[data-reveal]";

  const BODY_INTRO_CLASS = "dg-team-stange-intro-active";
  const BODY_MODAL_CLASS = "dg-team-stange-modal-open";

  const instances = new WeakMap();

  /* =========================================================
     HELPERS
     ========================================================= */

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function getReducedMotion() {
    return Boolean(
      window.matchMedia &&
        window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches
    );
  }

  function safeSessionGet(key) {
    if (!key) {
      return null;
    }

    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function safeSessionSet(key, value) {
    if (!key) {
      return;
    }

    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      /* Session storage may be unavailable. */
    }
  }

  function nextFrame(callback) {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(callback);
    });
  }

  function parseBoolean(value) {
    return value === true || value === "true";
  }

  function getFocusableElements(container) {
    if (!container) {
      return [];
    }

    return Array.from(
      container.querySelectorAll(
        [
          "a[href]",
          "button:not([disabled])",
          "input:not([disabled])",
          "select:not([disabled])",
          "textarea:not([disabled])",
          '[tabindex]:not([tabindex="-1"])'
        ].join(",")
      )
    ).filter((element) => {
      return (
        !element.hasAttribute("hidden") &&
        element.getAttribute("aria-hidden") !== "true"
      );
    });
  }

  /* =========================================================
     SHARED REVEAL ENGINE
     Mirrors the Analytics Terminal observer philosophy.
     ========================================================= */

  class DGTeamStangeReveal {
    constructor(root) {
      this.root = root;

      this.items = Array.from(
        root.querySelectorAll(REVEAL_SELECTOR)
      );

      this.observer = null;
      this.reducedMotion = getReducedMotion();

      this.init();
    }

    init() {
      if (!this.items.length) {
        return;
      }

      if (
        this.reducedMotion ||
        !("IntersectionObserver" in window)
      ) {
        this.showAll();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              return;
            }

            entry.target.classList.add("is-visible");

            observer.unobserve(entry.target);
          });
        },
        {
          threshold: 0.12,
          rootMargin: "0px 0px -8%"
        }
      );

      this.items.forEach((item) => {
        this.observer.observe(item);
      });
    }

    showAll() {
      this.items.forEach((item) => {
        item.classList.add("is-visible");
      });
    }

    refresh() {
      this.destroy();

      this.items = Array.from(
        this.root.querySelectorAll(REVEAL_SELECTOR)
      );

      this.reducedMotion = getReducedMotion();

      this.init();
    }

    destroy() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }
  }

  /* =========================================================
     HERO / CINEMATIC INTRO
     ========================================================= */

  class DGTeamStangeHero {
    constructor(section) {
      this.section = section;

      this.intro = section.querySelector(
        "[data-dgts-intro]"
      );

      this.introShell = section.querySelector(
        "[data-dgts-intro-shell]"
      );

      this.introVideo =
        this.intro?.querySelector("video") || null;

      this.hero = section.querySelector(
        "[data-dgts-hero]"
      );

      this.heroVideo =
        this.hero?.querySelector("video") || null;

      this.heroCopy = section.querySelector(
        "[data-dgts-hero-copy]"
      );

      this.soundToggle = section.querySelector(
        "[data-dgts-sound-toggle]"
      );

      this.soundLabel = section.querySelector(
        "[data-dgts-sound-label]"
      );

      this.soundIconMuted = section.querySelector(
        "[data-dgts-sound-icon-muted]"
      );

      this.soundIconLive = section.querySelector(
        "[data-dgts-sound-icon-live]"
      );

      this.skipButton = section.querySelector(
        "[data-dgts-skip]"
      );

      this.progress = section.querySelector(
        "[data-dgts-progress]"
      );

      this.introEnabled = parseBoolean(
        section.dataset.introEnabled
      );

      this.oncePerSession = parseBoolean(
        section.dataset.introOnce
      );

      this.sessionKey =
        section.dataset.introSessionKey || "";

      this.transitionDuration = clamp(
        Number(section.dataset.transitionDuration) || 1200,
        600,
        1800
      );

      this.reducedMotion = getReducedMotion();

      this.destroyed = false;
      this.introActive = false;
      this.transitioning = false;
      this.introCompleted = false;
      this.soundEnabled = false;

      this.progressFrame = null;
      this.transitionTimer = null;
      this.videoReadyTimer = null;

      this.originalOverflow = "";
      this.lockedScrollY = 0;
      this.originalBodyLockStyles = null;
      this.introOriginalParent = null;
      this.introOriginalNextSibling = null;

      this.onVideoEnded =
        this.handleVideoEnded.bind(this);

      this.onVideoTimeUpdate =
        this.handleVideoTimeUpdate.bind(this);

      this.onSkipClick =
        this.handleSkipClick.bind(this);

      this.onSoundClick =
        this.handleSoundClick.bind(this);

      this.onWindowResize =
        this.handleWindowResize.bind(this);

      this.onVisibilityChange =
        this.handleVisibilityChange.bind(this);

      this.onPlaybackRecovery =
        this.handlePlaybackRecovery.bind(this);

      this.init();
    }

    /* =========================================================
       INITIALIZATION
       ========================================================= */

    init() {
      this.section.classList.add("dg-ready");

      this.prepareHeroVideo();
      this.bindEvents();

      if (!this.shouldPlayIntro()) {
        this.showCompletedHero({
          rememberSession: false
        });

        return;
      }

      this.startIntro();
    }

    bindEvents() {
      if (this.introVideo) {
        this.introVideo.addEventListener(
          "ended",
          this.onVideoEnded
        );

        this.introVideo.addEventListener(
          "timeupdate",
          this.onVideoTimeUpdate
        );
      }

      if (this.skipButton) {
        this.skipButton.addEventListener(
          "click",
          this.onSkipClick
        );
      }

      if (this.soundToggle) {
        this.soundToggle.addEventListener(
          "click",
          this.onSoundClick
        );
      }

      window.addEventListener(
        "resize",
        this.onWindowResize,
        {
          passive: true
        }
      );

      document.addEventListener(
        "visibilitychange",
        this.onVisibilityChange
      );
    }

    shouldPlayIntro() {
      if (
        !this.introEnabled ||
        !this.intro ||
        !this.introVideo
      ) {
        return false;
      }

      if (this.reducedMotion) {
        return false;
      }

      if (
        this.oncePerSession &&
        safeSessionGet(this.sessionKey) === "played"
      ) {
        return false;
      }

      return true;
    }

    /* =========================================================
       HERO VIDEO
       ========================================================= */

    prepareHeroVideo() {
      if (!this.heroVideo) {
        return;
      }

      this.heroVideo.muted = true;
      this.heroVideo.defaultMuted = true;
      this.heroVideo.loop = true;
      this.heroVideo.playsInline = true;
      this.heroVideo.setAttribute("playsinline", "");
      this.heroVideo.setAttribute("webkit-playsinline", "");

      const playPromise =
        this.heroVideo.play();

      if (
        playPromise &&
        typeof playPromise.catch === "function"
      ) {
        playPromise.catch(() => {
          /*
           * Mobile browsers can block autoplay in low-power
           * or data-saving modes. The intro recovery gesture
           * will also restart the looping hero.
           */
        });
      }
    }

    armPlaybackRecovery() {
      if (!this.intro || !this.introVideo) {
        return;
      }

      this.section.classList.add(
        "is-playback-required"
      );

      this.intro.addEventListener(
        "pointerdown",
        this.onPlaybackRecovery,
        { passive: true }
      );
    }

    disarmPlaybackRecovery() {
      this.section.classList.remove(
        "is-playback-required"
      );

      if (this.intro) {
        this.intro.removeEventListener(
          "pointerdown",
          this.onPlaybackRecovery
        );
      }
    }

    handlePlaybackRecovery() {
      if (
        !this.introActive ||
        !this.introVideo ||
        this.transitioning
      ) {
        return;
      }

      this.introVideo.muted = true;
      this.introVideo.defaultMuted = true;
      this.introVideo.playsInline = true;
      this.introVideo.setAttribute(
        "playsinline",
        ""
      );
      this.introVideo.setAttribute(
        "webkit-playsinline",
        ""
      );

      const playPromise =
        this.introVideo.play();

      if (
        playPromise &&
        typeof playPromise.then === "function"
      ) {
        playPromise
          .then(() => {
            this.disarmPlaybackRecovery();
            this.startProgressLoop();

            if (this.heroVideo) {
              this.heroVideo.muted = true;
              this.heroVideo.play().catch(() => {});
            }
          })
          .catch(() => {
            /*
             * Keep the recovery prompt available so the
             * visitor can try again or use Skip Intro.
             */
          });
      } else {
        this.disarmPlaybackRecovery();
        this.startProgressLoop();
      }
    }

    syncHeroVideoTime() {
      if (
        !this.introVideo ||
        !this.heroVideo
      ) {
        return;
      }

      const introDuration =
        Number(this.introVideo.duration);

      const heroDuration =
        Number(this.heroVideo.duration);

      const introTime =
        Number(this.introVideo.currentTime);

      if (
        !Number.isFinite(introTime) ||
        introTime < 0
      ) {
        return;
      }

      let targetTime = introTime;

      if (
        Number.isFinite(heroDuration) &&
        heroDuration > 0
      ) {
        targetTime =
          introTime % heroDuration;
      } else if (
        Number.isFinite(introDuration) &&
        introDuration > 0
      ) {
        targetTime =
          Math.min(
            introTime,
            Math.max(0, introDuration - 0.05)
          );
      }

      try {
        this.heroVideo.currentTime =
          Math.max(0, targetTime);
      } catch (error) {
        /* Video may not be seekable yet. */
      }
    }

    /* =========================================================
       INTRO START
       ========================================================= */

    startIntro() {
      if (
        this.destroyed ||
        this.introActive
      ) {
        return;
      }

      this.introActive = true;
      this.transitioning = false;
      this.introCompleted = false;

      this.section.classList.add(
        "is-intro-active"
      );

      this.section.classList.remove(
        "is-intro-complete"
      );

      this.intro.setAttribute(
        "aria-hidden",
        "false"
      );

      this.mountIntroOverlay();

      this.lockPage();

      this.resetProgress();
      this.setMutedState();

      this.introVideo.muted = true;
      this.introVideo.defaultMuted = true;
      this.introVideo.playsInline = true;
      this.introVideo.setAttribute("playsinline", "");
      this.introVideo.setAttribute("webkit-playsinline", "");

      try {
        this.introVideo.currentTime = 0;
      } catch (error) {
        /* Ignore non-seekable state. */
      }

      const playPromise =
        this.introVideo.play();

      if (
        playPromise &&
        typeof playPromise.then === "function"
      ) {
        playPromise
          .then(() => {
            this.startProgressLoop();
          })
          .catch(() => {
            /*
             * Mobile Safari can block autoplay in low-power
             * mode. Keep the film available and recover on
             * the visitor's first touch instead of skipping.
             */
            this.armPlaybackRecovery();
          });
      } else {
        this.startProgressLoop();
      }

      nextFrame(() => {
        if (!this.destroyed) {
          this.section.classList.add(
            "is-intro-visible"
          );
        }
      });
    }

    /* =========================================================
       AUDIO
       ========================================================= */

    handleSoundClick() {
      if (
        !this.introVideo ||
        !this.introActive ||
        this.transitioning
      ) {
        return;
      }

      this.soundEnabled =
        !this.soundEnabled;

      this.introVideo.muted =
        !this.soundEnabled;

      if (this.soundEnabled) {
        this.introVideo.volume = 1;

        const playPromise =
          this.introVideo.play();

        if (
          playPromise &&
          typeof playPromise.catch === "function"
        ) {
          playPromise.catch(() => {
            this.soundEnabled = false;
            this.introVideo.muted = true;
            this.setMutedState();
          });
        }
      }

      this.updateSoundUI();
    }

    setMutedState() {
      this.soundEnabled = false;

      if (this.introVideo) {
        this.introVideo.muted = true;
      }

      this.updateSoundUI();
    }

    updateSoundUI() {
      if (!this.soundToggle) {
        return;
      }

      const live =
        this.soundEnabled &&
        this.introVideo &&
        !this.introVideo.muted;

      this.soundToggle.setAttribute(
        "aria-pressed",
        live ? "true" : "false"
      );

      if (this.soundIconMuted) {
        this.soundIconMuted.hidden = live;
      }

      if (this.soundIconLive) {
        this.soundIconLive.hidden = !live;
      }

      if (this.soundLabel) {
        const onLabel =
          this.section.dataset.soundOnLabel ||
          "Play With Sound";

        const offLabel =
          this.section.dataset.soundOffLabel ||
          "Mute";

        this.soundLabel.textContent =
          live ? offLabel : onLabel;
      }

      this.soundToggle.setAttribute(
        "aria-label",
        live
          ? (
              this.section.dataset.soundOffLabel ||
              "Mute"
            )
          : (
              this.section.dataset.soundOnLabel ||
              "Play With Sound"
            )
      );
    }

    /* =========================================================
       INTRO PROGRESS
       ========================================================= */

    resetProgress() {
      if (!this.progress) {
        return;
      }

      this.progress.style.width = "0%";
      this.progress.style.transform =
        "scaleX(1)";
    }

    startProgressLoop() {
      this.stopProgressLoop();

      const update = () => {
        if (
          this.destroyed ||
          !this.introActive ||
          this.transitioning
        ) {
          return;
        }

        this.updateProgress();

        this.progressFrame =
          window.requestAnimationFrame(update);
      };

      this.progressFrame =
        window.requestAnimationFrame(update);
    }

    stopProgressLoop() {
      if (this.progressFrame) {
        window.cancelAnimationFrame(
          this.progressFrame
        );

        this.progressFrame = null;
      }
    }

    updateProgress() {
      if (
        !this.progress ||
        !this.introVideo
      ) {
        return;
      }

      const duration =
        Number(this.introVideo.duration);

      const current =
        Number(this.introVideo.currentTime);

      if (
        !Number.isFinite(duration) ||
        duration <= 0 ||
        !Number.isFinite(current)
      ) {
        return;
      }

      const percentage =
        clamp(
          (current / duration) * 100,
          0,
          100
        );

      this.progress.style.width =
        `${percentage.toFixed(2)}%`;
    }

    handleVideoTimeUpdate() {
      this.updateProgress();
    }

    /* =========================================================
       COMPLETE / SKIP
       ========================================================= */

    handleVideoEnded() {
      this.finishIntro({
        skipped: false
      });
    }

    handleSkipClick() {
      this.finishIntro({
        skipped: true
      });
    }

    finishIntro({
      skipped = false,
      autoplayFailed = false
    } = {}) {
      this.disarmPlaybackRecovery();
      if (
        this.destroyed ||
        !this.introActive ||
        this.transitioning
      ) {
        return;
      }

      this.transitioning = true;

      this.stopProgressLoop();

      if (this.progress) {
        this.progress.style.width = "100%";
      }

      if (
        this.oncePerSession &&
        !autoplayFailed
      ) {
        safeSessionSet(
          this.sessionKey,
          "played"
        );
      }

      this.syncHeroVideoTime();

      if (this.heroVideo) {
        this.heroVideo.muted = true;
        this.heroVideo.defaultMuted = true;

        const heroPlayPromise =
          this.heroVideo.play();

        if (
          heroPlayPromise &&
          typeof heroPlayPromise.catch === "function"
        ) {
          heroPlayPromise.catch(() => {});
        }
      }

      if (
        skipped &&
        this.introVideo &&
        !this.introVideo.paused
      ) {
        /*
         * Leave video running during morph so the
         * transition still feels continuous.
         */
      }

      this.beginMorph();
    }

    /* =========================================================
       FULLSCREEN → HERO MORPH
       ========================================================= */

    beginMorph() {
      if (
        !this.introShell ||
        !this.hero
      ) {
        this.completeMorph();
        return;
      }

      const introRect =
        this.introShell.getBoundingClientRect();

      const heroRect =
        this.hero.getBoundingClientRect();

      if (
        !introRect.width ||
        !introRect.height ||
        !heroRect.width ||
        !heroRect.height
      ) {
        this.completeMorph();
        return;
      }

      this.section.classList.add(
        "is-intro-transitioning"
      );

      /*
       * The hero normally occupies the viewport width,
       * but calculating the exact destination keeps the
       * transition correct if theme layout changes.
       */
      const targetLeft =
        heroRect.left - introRect.left;

      const targetTop =
        heroRect.top - introRect.top;

      const scaleX =
        heroRect.width / introRect.width;

      const scaleY =
        heroRect.height / introRect.height;

      /*
       * CSS transform cannot independently scale X/Y
       * without distorting video. We instead animate
       * shell dimensions and translation together.
       */
      this.introShell.style.transition = [
        `left ${this.transitionDuration}ms var(--dg-ease-emphasized)`,
        `top ${this.transitionDuration}ms var(--dg-ease-emphasized)`,
        `width ${this.transitionDuration}ms var(--dg-ease-emphasized)`,
        `height ${this.transitionDuration}ms var(--dg-ease-emphasized)`,
        `border-radius ${this.transitionDuration}ms var(--dg-ease-emphasized)`,
        `opacity ${Math.round(
          this.transitionDuration * 0.72
        )}ms var(--dg-ease-standard)`
      ].join(", ");

      /*
       * Scale values are intentionally calculated even
       * though dimensions drive the morph. They provide
       * a useful validity check for unusual layouts.
       */
      if (
        !Number.isFinite(scaleX) ||
        !Number.isFinite(scaleY)
      ) {
        this.completeMorph();
        return;
      }

      nextFrame(() => {
        if (this.destroyed) {
          return;
        }

        this.introShell.style.left =
          `${targetLeft}px`;

        this.introShell.style.top =
          `${targetTop}px`;

        this.introShell.style.width =
          `${heroRect.width}px`;

        this.introShell.style.height =
          `${heroRect.height}px`;

        this.introShell.style.borderRadius =
          "0px";

        /*
         * Slight opacity handoff lets the looping hero
         * video replace the intro video invisibly.
         */
        this.transitionTimer =
          window.setTimeout(() => {
            this.introShell.style.opacity =
              "0";

            window.setTimeout(
              () => {
                this.completeMorph();
              },
              Math.max(
                180,
                Math.round(
                  this.transitionDuration * 0.28
                )
              )
            );
          }, Math.round(
            this.transitionDuration * 0.72
          ));
      });
    }

    completeMorph() {
      if (this.destroyed) {
        return;
      }

      this.transitioning = false;
      this.introActive = false;
      this.introCompleted = true;

      if (this.introVideo) {
        this.introVideo.pause();
        this.introVideo.muted = true;
      }

      this.section.classList.remove(
        "is-intro-active",
        "is-intro-visible",
        "is-intro-transitioning"
      );

      this.section.classList.add(
        "is-intro-complete"
      );

      if (this.intro) {
        this.intro.setAttribute(
          "aria-hidden",
          "true"
        );
      }

      this.resetIntroShell();
      this.restoreIntroOverlay();
      this.unlockPage();

      /*
       * Hero reveals are already observed. Force any
       * hero copy that was intentionally hidden during
       * intro to become eligible immediately.
       */
      const heroRevealItems = Array.from(
        this.section.querySelectorAll(
          ".dg-team-stange-hero__copy [data-reveal]"
        )
      );

      heroRevealItems.forEach((item) => {
        item.classList.add("is-visible");
      });
    }

    showCompletedHero({
      rememberSession = false
    } = {}) {
      this.stopProgressLoop();

      this.introActive = false;
      this.transitioning = false;
      this.introCompleted = true;

      if (
        rememberSession &&
        this.oncePerSession
      ) {
        safeSessionSet(
          this.sessionKey,
          "played"
        );
      }

      if (this.introVideo) {
        this.introVideo.pause();
        this.introVideo.muted = true;
      }

      if (this.intro) {
        this.intro.setAttribute(
          "aria-hidden",
          "true"
        );
      }

      this.section.classList.remove(
        "is-intro-active",
        "is-intro-visible",
        "is-intro-transitioning"
      );

      this.section.classList.add(
        "is-intro-complete"
      );

      this.resetIntroShell();
      this.restoreIntroOverlay();
      this.unlockPage();

      const heroRevealItems = Array.from(
        this.section.querySelectorAll(
          ".dg-team-stange-hero__copy [data-reveal]"
        )
      );

      heroRevealItems.forEach((item) => {
        item.classList.add("is-visible");
      });
    }

    resetIntroShell() {
      if (!this.introShell) {
        return;
      }

      this.introShell.style.transition = "";
      this.introShell.style.left = "";
      this.introShell.style.top = "";
      this.introShell.style.width = "";
      this.introShell.style.height = "";
      this.introShell.style.opacity = "";
      this.introShell.style.borderRadius = "";
      this.introShell.style.transform = "";
    }

    /* =========================================================
       PAGE LOCK
       ========================================================= */

    mountIntroOverlay() {
      if (!this.intro || this.intro.parentNode === document.body) {
        return;
      }

      this.introOriginalParent = this.intro.parentNode;
      this.introOriginalNextSibling = this.intro.nextSibling;
      document.body.appendChild(this.intro);
    }

    restoreIntroOverlay() {
      if (!this.intro || !this.introOriginalParent) {
        return;
      }

      if (
        this.introOriginalNextSibling &&
        this.introOriginalNextSibling.parentNode ===
          this.introOriginalParent
      ) {
        this.introOriginalParent.insertBefore(
          this.intro,
          this.introOriginalNextSibling
        );
      } else {
        this.introOriginalParent.appendChild(this.intro);
      }

      this.introOriginalParent = null;
      this.introOriginalNextSibling = null;
    }

    lockPage() {
      const root = document.documentElement;
      const body = document.body;

      if (root.classList.contains(BODY_INTRO_CLASS)) {
        return;
      }

      this.lockedScrollY =
        window.scrollY || root.scrollTop || 0;
      this.originalOverflow =
        root.style.overflow;
      this.originalBodyLockStyles = {
        position: body.style.position,
        top: body.style.top,
        left: body.style.left,
        right: body.style.right,
        width: body.style.width,
        overflow: body.style.overflow
      };

      root.classList.add(BODY_INTRO_CLASS);
      body.classList.add(BODY_INTRO_CLASS);

      root.style.overflow = "hidden";
      body.style.position = "fixed";
      body.style.top = `-${this.lockedScrollY}px`;
      body.style.left = "0";
      body.style.right = "0";
      body.style.width = "100vw";
      body.style.overflow = "hidden";
    }

    unlockPage() {
      const root = document.documentElement;
      const body = document.body;
      const restoreScrollY =
        this.lockedScrollY || 0;
      const saved =
        this.originalBodyLockStyles;

      root.classList.remove(BODY_INTRO_CLASS);
      body.classList.remove(BODY_INTRO_CLASS);

      if (saved) {
        body.style.position = saved.position;
        body.style.top = saved.top;
        body.style.left = saved.left;
        body.style.right = saved.right;
        body.style.width = saved.width;
        body.style.overflow = saved.overflow;
      }

      if (!body.classList.contains(BODY_MODAL_CLASS)) {
        root.style.overflow =
          this.originalOverflow || "";
      }

      this.originalBodyLockStyles = null;
      window.scrollTo(0, restoreScrollY);
    }

    /* =========================================================
       VISIBILITY / RESIZE
       ========================================================= */

    handleWindowResize() {
      /*
       * If viewport changes during the morph, completing
       * it immediately is cleaner than morphing toward a
       * stale destination rectangle.
       */
      if (this.transitioning) {
        this.completeMorph();
      }
    }

    handleVisibilityChange() {
      if (
        document.hidden ||
        !this.introVideo ||
        !this.introActive ||
        this.transitioning
      ) {
        return;
      }

      const playPromise =
        this.introVideo.play();

      if (
        playPromise &&
        typeof playPromise.catch === "function"
      ) {
        playPromise.catch(() => {});
      }
    }

    /* =========================================================
       DESTROY
       ========================================================= */

    destroy() {
      this.destroyed = true;
      this.disarmPlaybackRecovery();

      this.stopProgressLoop();

      if (this.transitionTimer) {
        window.clearTimeout(
          this.transitionTimer
        );

        this.transitionTimer = null;
      }

      if (this.videoReadyTimer) {
        window.clearTimeout(
          this.videoReadyTimer
        );

        this.videoReadyTimer = null;
      }

      if (this.introVideo) {
        this.introVideo.removeEventListener(
          "ended",
          this.onVideoEnded
        );

        this.introVideo.removeEventListener(
          "timeupdate",
          this.onVideoTimeUpdate
        );

        this.introVideo.pause();
      }

      if (this.skipButton) {
        this.skipButton.removeEventListener(
          "click",
          this.onSkipClick
        );
      }

      if (this.soundToggle) {
        this.soundToggle.removeEventListener(
          "click",
          this.onSoundClick
        );
      }

      window.removeEventListener(
        "resize",
        this.onWindowResize
      );

      document.removeEventListener(
        "visibilitychange",
        this.onVisibilityChange
      );

      this.unlockPage();
      this.restoreIntroOverlay();
      this.resetIntroShell();
    }
  }

  /* =========================================================
     HERO INITIALIZER
     ========================================================= */

  function initHero(section) {
    if (!section) {
      return null;
    }

    const existing =
      instances.get(section);

    if (existing) {
      return existing;
    }

    const reveal =
      new DGTeamStangeReveal(section);

    const hero =
      new DGTeamStangeHero(section);

    const instance = {
      reveal,
      hero,

      destroy() {
        reveal.destroy();
        hero.destroy();
      }
    };

    instances.set(
      section,
      instance
    );

    return instance;
  }

  function destroyHero(section) {
    if (!section) {
      return;
    }

    const instance =
      instances.get(section);

    if (!instance) {
      return;
    }

    instance.destroy();

    instances.delete(section);
  }

  /* =========================================================
     INITIAL PAGE LOAD
     ========================================================= */

  function initHeroSections(root = document) {
    const sections = [];

    if (
      root.matches &&
      root.matches(HERO_SELECTOR)
    ) {
      sections.push(root);
    }

    if (root.querySelectorAll) {
      root
        .querySelectorAll(HERO_SELECTOR)
        .forEach((section) => {
          sections.push(section);
        });
    }

    sections.forEach((section) => {
      initHero(section);
    });
  }

  if (
    document.readyState === "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        initHeroSections(document);
      },
      {
        once: true
      }
    );
  } else {
    initHeroSections(document);
  }

  /* =========================================================
     SHOPIFY THEME EDITOR
     ========================================================= */

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initHeroSections(event.target);
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      const section =
        event.target.matches?.(
          HERO_SELECTOR
        )
          ? event.target
          : event.target.querySelector?.(
              HERO_SELECTOR
            );

      destroyHero(section);
    }
  );

    /* =========================================================
     MACHINE HOTSPOTS
     ========================================================= */

  class DGTeamStangeMachine {
    constructor(section) {
      this.section = section;

      this.hotspots = Array.from(
        section.querySelectorAll(
          "[data-dgts-hotspot]"
        )
      );

      this.annotations = Array.from(
        section.querySelectorAll(
          "[data-dgts-annotation]"
        )
      );

      this.activeIndex = 1;

      this.onHotspotClick =
        this.handleHotspotClick.bind(this);

      this.onHotspotFocus =
        this.handleHotspotFocus.bind(this);

      this.onAnnotationEnter =
        this.handleAnnotationEnter.bind(this);

      this.init();
    }

    init() {
      if (
        !this.hotspots.length ||
        !this.annotations.length
      ) {
        return;
      }

      this.hotspots.forEach((hotspot) => {
        hotspot.addEventListener(
          "click",
          this.onHotspotClick
        );

        hotspot.addEventListener(
          "focus",
          this.onHotspotFocus
        );
      });
      this.annotations.forEach((annotation) => {
        annotation.tabIndex = 0;
        annotation.addEventListener("mouseenter", this.onAnnotationEnter);
        annotation.addEventListener("focus", this.onAnnotationEnter);
        annotation.addEventListener("click", this.onAnnotationEnter);
      });


      this.setActive(1, {
        focus: false
      });
    }

    handleHotspotClick(event) {
      const hotspot =
        event.currentTarget;

      const index =
        Number(
          hotspot.dataset.hotspotIndex
        );

      this.setActive(index, {
        focus: false
      });
    }

    handleHotspotFocus(event) {
      const hotspot =
        event.currentTarget;

      const index =
        Number(
          hotspot.dataset.hotspotIndex
        );

      this.setActive(index, {
        focus: false
      });
    }

    handleAnnotationEnter(event) {
      const annotation = event.currentTarget;
      const index = Number(annotation.dataset.annotationIndex);
      this.setActive(index, { focus: false });
    }

    setActive(
      index,
      {
        focus = false
      } = {}
    ) {
      if (
        !Number.isFinite(index) ||
        index < 1
      ) {
        return;
      }

      this.activeIndex = index;

      this.hotspots.forEach(
        (hotspot) => {
          const hotspotIndex =
            Number(
              hotspot.dataset.hotspotIndex
            );

          const active =
            hotspotIndex === index;

          hotspot.setAttribute(
            "aria-expanded",
            active ? "true" : "false"
          );

          hotspot.classList.toggle(
            "is-active",
            active
          );

          if (
            active &&
            focus
          ) {
            hotspot.focus({
              preventScroll: true
            });
          }
        }
      );

      this.annotations.forEach(
        (annotation) => {
          const annotationIndex =
            Number(
              annotation.dataset.annotationIndex
            );

          annotation.classList.toggle(
            "is-active",
            annotationIndex === index
          );
        }
      );
    }

    destroy() {
      this.hotspots.forEach((hotspot) => {
        hotspot.removeEventListener(
          "click",
          this.onHotspotClick
        );

        hotspot.removeEventListener(
          "focus",
          this.onHotspotFocus
        );
      });
      this.annotations.forEach((annotation) => {
        annotation.removeEventListener("mouseenter", this.onAnnotationEnter);
        annotation.removeEventListener("focus", this.onAnnotationEnter);
        annotation.removeEventListener("click", this.onAnnotationEnter);
      });

    }
  }


  /* =========================================================
     TELEMETRY COUNTERS
     ========================================================= */

  class DGTeamStangeTelemetry {
    constructor(section) {
      this.section = section;

      this.metrics = Array.from(
        section.querySelectorAll(
          "[data-dgts-metric-value]"
        )
      );

      this.observer = null;

      this.animated = new WeakSet();

      this.reducedMotion =
        getReducedMotion();

      this.init();
    }

    init() {
      if (!this.metrics.length) {
        return;
      }

      if (
        this.reducedMotion ||
        !(
          "IntersectionObserver" in
          window
        )
      ) {
        this.metrics.forEach(
          (metric) => {
            this.showFinalValue(metric);
          }
        );

        return;
      }

      this.observer =
        new IntersectionObserver(
          (entries, observer) => {
            entries.forEach(
              (entry) => {
                if (
                  !entry.isIntersecting
                ) {
                  return;
                }

                this.animateMetric(
                  entry.target
                );

                observer.unobserve(
                  entry.target
                );
              }
            );
          },
          {
            threshold: 0.35,
            rootMargin:
              "0px 0px -6%"
          }
        );

      this.metrics.forEach(
        (metric) => {
          this.observer.observe(
            metric
          );
        }
      );
    }

    showFinalValue(metric) {
      metric.textContent =
        metric.dataset.value || "";
    }

    parseMetric(value) {
      const trimmed =
        String(value || "").trim();

      const match =
        trimmed.match(
          /^([0-9]+(?:\.[0-9]+)?)(.*)$/
        );

      if (!match) {
        return null;
      }

      return {
        number:
          Number(match[1]),

        suffix:
          match[2] || "",

        decimals:
          match[1].includes(".")
            ? match[1].split(".")[1].length
            : 0
      };
    }

    animateMetric(metric) {
      if (
        this.animated.has(metric)
      ) {
        return;
      }

      this.animated.add(metric);

      const shouldAnimate =
        parseBoolean(
          metric.dataset.animate
        );

      const rawValue =
        metric.dataset.value || "";

      const parsed =
        this.parseMetric(rawValue);

      if (
        !shouldAnimate ||
        !parsed
      ) {
        metric.textContent =
          rawValue;

        return;
      }

      const duration = 1500;

      const start =
        performance.now();

      const update = (time) => {
        const progress =
          clamp(
            (time - start) /
              duration,
            0,
            1
          );

        const eased =
          1 -
          Math.pow(
            1 - progress,
            3
          );

        const current =
          parsed.number * eased;

        metric.textContent =
          `${current.toFixed(
            parsed.decimals
          )}${parsed.suffix}`;

        if (progress < 1) {
          window.requestAnimationFrame(
            update
          );

          return;
        }

        metric.textContent =
          rawValue;
      };

      window.requestAnimationFrame(
        update
      );
    }

    destroy() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }
  }


  /* =========================================================
     TRACKSIDE GALLERY
     ========================================================= */

  class DGTeamStangeGallery {
    constructor(section) {
      this.section = section;

      this.track =
        section.querySelector(
          "[data-dgts-gallery-track]"
        );

      this.items = Array.from(
        section.querySelectorAll(
          "[data-dgts-gallery-item]"
        )
      );

      this.openButtons = Array.from(
        section.querySelectorAll(
          "[data-dgts-gallery-open]"
        )
      );

      this.previousButton =
        section.querySelector(
          "[data-dgts-gallery-prev]"
        );

      this.nextButton =
        section.querySelector(
          "[data-dgts-gallery-next]"
        );

      this.currentCounter =
        section.querySelector(
          "[data-dgts-gallery-current]"
        );

      this.lightbox =
        section.querySelector(
          "[data-dgts-gallery-lightbox]"
        );

      this.lightboxItems = Array.from(
        section.querySelectorAll(
          "[data-dgts-lightbox-item]"
        )
      );

      this.lightboxPrevious =
        section.querySelector(
          "[data-dgts-lightbox-prev]"
        );

      this.lightboxNext =
        section.querySelector(
          "[data-dgts-lightbox-next]"
        );

      this.lightboxCurrent =
        section.querySelector(
          "[data-dgts-lightbox-current]"
        );

      this.closeButtons = Array.from(
        section.querySelectorAll(
          "[data-dgts-gallery-close]"
        )
      );

      this.currentIndex = 1;
      this.lightboxIndex = 1;

      this.previousFocus = null;
      this.savedOverflow = "";

      this.onPrevious =
        () => this.moveGallery(-1);

      this.onNext =
        () => this.moveGallery(1);

      this.onTrackScroll =
        this.handleTrackScroll.bind(this);

      this.onOpen =
        this.handleOpen.bind(this);

      this.onLightboxPrevious =
        () => this.moveLightbox(-1);

      this.onLightboxNext =
        () => this.moveLightbox(1);

      this.onClose =
        this.closeLightbox.bind(this);

      this.onKeyDown =
        this.handleKeyDown.bind(this);

      this.init();
    }

    init() {
      if (!this.track) {
        return;
      }

      this.previousButton?.addEventListener(
        "click",
        this.onPrevious
      );

      this.nextButton?.addEventListener(
        "click",
        this.onNext
      );

      this.track.addEventListener(
        "scroll",
        this.onTrackScroll,
        {
          passive: true
        }
      );

      this.openButtons.forEach(
        (button) => {
          button.addEventListener(
            "click",
            this.onOpen
          );
        }
      );

      this.lightboxPrevious
        ?.addEventListener(
          "click",
          this.onLightboxPrevious
        );

      this.lightboxNext
        ?.addEventListener(
          "click",
          this.onLightboxNext
        );

      this.closeButtons.forEach(
        (button) => {
          button.addEventListener(
            "click",
            this.onClose
          );
        }
      );

      this.updateGalleryCounter();
    }

    getScrollAmount() {
      const firstItem =
        this.items[0];

      if (!firstItem) {
        return 400;
      }

      const style =
        window.getComputedStyle(
          this.track
        );

      const gap =
        parseFloat(
          style.columnGap ||
            style.gap ||
            "0"
        ) || 0;

      return (
        firstItem.getBoundingClientRect()
          .width + gap
      );
    }

    moveGallery(direction) {
      if (!this.track) {
        return;
      }

      this.track.scrollBy({
        left:
          this.getScrollAmount() *
          direction,

        behavior:
          getReducedMotion()
            ? "auto"
            : "smooth"
      });
    }

    handleTrackScroll() {
      if (
        !this.track ||
        !this.items.length
      ) {
        return;
      }

      const trackRect =
        this.track.getBoundingClientRect();

      let nearestIndex = 1;
      let nearestDistance =
        Infinity;

      this.items.forEach(
        (item, index) => {
          const rect =
            item.getBoundingClientRect();

          const distance =
            Math.abs(
              rect.left -
                trackRect.left
            );

          if (
            distance <
            nearestDistance
          ) {
            nearestDistance =
              distance;

            nearestIndex =
              index + 1;
          }
        }
      );

      this.currentIndex =
        nearestIndex;

      this.updateGalleryCounter();
    }

    updateGalleryCounter() {
      if (!this.currentCounter) {
        return;
      }

      this.currentCounter.textContent =
        String(
          this.currentIndex
        ).padStart(2, "0");
    }

    handleOpen(event) {
      const button =
        event.currentTarget;

      const index =
        Number(
          button.dataset.galleryIndex
        );

      this.openLightbox(
        index,
        button
      );
    }

    openLightbox(
      index,
      sourceButton
    ) {
      if (
        !this.lightbox ||
        !this.lightboxItems.length
      ) {
        return;
      }

      this.previousFocus =
        sourceButton ||
        document.activeElement;

      this.lightboxIndex =
        clamp(
          index || 1,
          1,
          this.lightboxItems.length
        );

      this.updateLightbox();

      this.lightbox.hidden = false;

      this.savedOverflow =
        document.documentElement.style
          .overflow;

      document.body.classList.add(
        BODY_MODAL_CLASS
      );

      document.documentElement.style
        .overflow = "hidden";

      document.addEventListener(
        "keydown",
        this.onKeyDown
      );

      nextFrame(() => {
        const focusables =
          getFocusableElements(
            this.lightbox
          );

        const close =
          focusables.find(
            (element) =>
              element.matches(
                "[data-dgts-gallery-close]"
              )
          );

        (
          close ||
          focusables[0]
        )?.focus({
          preventScroll: true
        });
      });
    }

    closeLightbox() {
      if (
        !this.lightbox ||
        this.lightbox.hidden
      ) {
        return;
      }

      this.lightbox.hidden = true;

      document.body.classList.remove(
        BODY_MODAL_CLASS
      );

      document.removeEventListener(
        "keydown",
        this.onKeyDown
      );

      if (
        !document.body.classList.contains(
          BODY_INTRO_CLASS
        )
      ) {
        document.documentElement.style
          .overflow =
          this.savedOverflow || "";
      }

      if (
        this.previousFocus &&
        typeof this.previousFocus.focus ===
          "function"
      ) {
        this.previousFocus.focus({
          preventScroll: true
        });
      }
    }

    moveLightbox(direction) {
      const total =
        this.lightboxItems.length;

      if (!total) {
        return;
      }

      this.lightboxIndex =
        (
          this.lightboxIndex -
          1 +
          direction +
          total
        ) %
          total +
        1;

      this.updateLightbox();
    }

    updateLightbox() {
      this.lightboxItems.forEach(
        (item) => {
          const index =
            Number(
              item.dataset.galleryIndex
            );

          item.hidden =
            index !==
            this.lightboxIndex;
        }
      );

      if (this.lightboxCurrent) {
        this.lightboxCurrent
          .textContent =
          String(
            this.lightboxIndex
          ).padStart(2, "0");
      }
    }

    handleKeyDown(event) {
      if (
        !this.lightbox ||
        this.lightbox.hidden
      ) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        this.closeLightbox();
        return;
      }

      if (
        event.key ===
        "ArrowLeft"
      ) {
        event.preventDefault();
        this.moveLightbox(-1);
        return;
      }

      if (
        event.key ===
        "ArrowRight"
      ) {
        event.preventDefault();
        this.moveLightbox(1);
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusables =
        getFocusableElements(
          this.lightbox
        );

      if (!focusables.length) {
        event.preventDefault();
        return;
      }

      const first =
        focusables[0];

      const last =
        focusables[
          focusables.length - 1
        ];

      if (
        event.shiftKey &&
        document.activeElement ===
          first
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement ===
          last
      ) {
        event.preventDefault();
        first.focus();
      }
    }

    destroy() {
      this.previousButton
        ?.removeEventListener(
          "click",
          this.onPrevious
        );

      this.nextButton
        ?.removeEventListener(
          "click",
          this.onNext
        );

      this.track?.removeEventListener(
        "scroll",
        this.onTrackScroll
      );

      this.openButtons.forEach(
        (button) => {
          button.removeEventListener(
            "click",
            this.onOpen
          );
        }
      );

      this.lightboxPrevious
        ?.removeEventListener(
          "click",
          this.onLightboxPrevious
        );

      this.lightboxNext
        ?.removeEventListener(
          "click",
          this.onLightboxNext
        );

      this.closeButtons.forEach(
        (button) => {
          button.removeEventListener(
            "click",
            this.onClose
          );
        }
      );

      document.removeEventListener(
        "keydown",
        this.onKeyDown
      );

      if (
        this.lightbox &&
        !this.lightbox.hidden
      ) {
        this.closeLightbox();
      }
    }
  }


  /* =========================================================
     NON-HERO SECTION REGISTRY
     ========================================================= */

  const componentInstances =
    new WeakMap();

  function initComponentSection(
    section
  ) {
    if (!section) {
      return null;
    }

    const existing =
      componentInstances.get(
        section
      );

    if (existing) {
      return existing;
    }

    const reveal =
      new DGTeamStangeReveal(
        section
      );

    let component = null;

    if (
      section.matches(
        "[data-dg-team-stange-machine]"
      )
    ) {
      component =
        new DGTeamStangeMachine(
          section
        );
    }

    if (
      section.matches(
        "[data-dg-team-stange-telemetry]"
      )
    ) {
      component =
        new DGTeamStangeTelemetry(
          section
        );
    }

    if (
      section.matches(
        "[data-dg-team-stange-gallery]"
      )
    ) {
      component =
        new DGTeamStangeGallery(
          section
        );
    }

    const instance = {
      reveal,
      component,

      destroy() {
        reveal.destroy();

        if (
          component &&
          typeof component.destroy ===
            "function"
        ) {
          component.destroy();
        }
      }
    };

    componentInstances.set(
      section,
      instance
    );

    return instance;
  }

  function destroyComponentSection(
    section
  ) {
    if (!section) {
      return;
    }

    const instance =
      componentInstances.get(
        section
      );

    if (!instance) {
      return;
    }

    instance.destroy();

    componentInstances.delete(
      section
    );
  }

  function initComponentSections(
    root = document
  ) {
    const selectors = [
      "[data-dg-team-stange-partnership]",
      "[data-dg-team-stange-values]",
      "[data-dg-team-stange-machine]",
      "[data-dg-team-stange-telemetry]",
      "[data-dg-team-stange-gallery]",
      "[data-dg-team-stange-forward]",
      "[data-dg-team-stange-closing]"
    ];

    selectors.forEach(
      (selector) => {
        const sections = [];

        if (
          root.matches &&
          root.matches(selector)
        ) {
          sections.push(root);
        }

        if (root.querySelectorAll) {
          root
            .querySelectorAll(
              selector
            )
            .forEach(
              (section) => {
                sections.push(
                  section
                );
              }
            );
        }

        sections.forEach(
          (section) => {
            initComponentSection(
              section
            );
          }
        );
      }
    );
  }

  /* =========================================================
     INITIALIZE NON-HERO SECTIONS
     ========================================================= */

  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        initComponentSections(
          document
        );
      },
      {
        once: true
      }
    );
  } else {
    initComponentSections(
      document
    );
  }

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initComponentSections(
        event.target
      );
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      const selectors = [
        "[data-dg-team-stange-partnership]",
        "[data-dg-team-stange-values]",
        "[data-dg-team-stange-machine]",
        "[data-dg-team-stange-telemetry]",
        "[data-dg-team-stange-gallery]",
        "[data-dg-team-stange-forward]",
        "[data-dg-team-stange-closing]"
      ];

      selectors.forEach(
        (selector) => {
          const section =
            event.target.matches?.(
              selector
            )
              ? event.target
              : event.target
                  .querySelector?.(
                    selector
                  );

          destroyComponentSection(
            section
          );
        }
      );
    }
  );

    /* =========================================================
     FEATURED FILMS
     01 / 03 editorial navigation
     Lazy YouTube iframe
     Swipe + keyboard
     Accessible modal
     ========================================================= */

  class DGTeamStangeFilm {
    constructor(section) {
      this.section = section;

      this.viewer =
        section.querySelector(
          "[data-film-viewer]"
        );

      this.stage =
        section.querySelector(
          "[data-film-stage]"
        );

      this.slides = Array.from(
        section.querySelectorAll(
          "[data-film-slide]"
        )
      );

      this.previousButtons =
        Array.from(
          section.querySelectorAll(
            "[data-film-prev]"
          )
        );

      this.nextButtons =
        Array.from(
          section.querySelectorAll(
            "[data-film-next]"
          )
        );

      this.openButtons =
        Array.from(
          section.querySelectorAll(
            "[data-film-open]"
          )
        );

      this.modal =
        section.querySelector(
          "[data-film-modal]"
        );

      this.player =
        section.querySelector(
          "[data-film-player]"
        );

      this.closeButtons =
        Array.from(
          section.querySelectorAll(
            "[data-film-close]"
          )
        );

      this.currentIndex = 0;

      this.transitioning = false;
      this.transitionTimer = null;

      this.previousFocus = null;
      this.savedOverflow = "";

      this.reducedMotion =
        getReducedMotion();

      this.destroyed = false;

      this.onPrevious =
        this.handlePrevious.bind(this);

      this.onNext =
        this.handleNext.bind(this);

      this.onOpen =
        this.handleOpen.bind(this);

      this.onClose =
        this.closeModal.bind(this);

      this.onSectionKeyDown =
        this.handleSectionKeyDown.bind(this);

      this.onModalKeyDown =
        this.handleModalKeyDown.bind(this);

      this.init();
    }

    /* =========================================================
       INITIALIZATION
       ========================================================= */

    init() {
      if (
        !this.viewer ||
        !this.slides.length
      ) {
        return;
      }

      const initialIndex =
        this.slides.findIndex(
          (slide) =>
            slide.classList.contains(
              "is-active"
            )
        );

      this.currentIndex =
        initialIndex >= 0
          ? initialIndex
          : 0;

      this.updateSlides({
        immediate: true
      });

      this.bindEvents();
      this.preloadAdjacentPosters();
    }

    bindEvents() {
      this.previousButtons.forEach(
        (button) => {
          button.addEventListener(
            "click",
            this.onPrevious
          );
        }
      );

      this.nextButtons.forEach(
        (button) => {
          button.addEventListener(
            "click",
            this.onNext
          );
        }
      );

      this.openButtons.forEach(
        (button) => {
          button.addEventListener(
            "click",
            this.onOpen
          );
        }
      );

      this.closeButtons.forEach(
        (button) => {
          button.addEventListener(
            "click",
            this.onClose
          );
        }
      );

      this.section.addEventListener(
        "keydown",
        this.onSectionKeyDown
      );

      if (this.stage) {
        this.stage.addEventListener(
          "pointerdown",
          this.onPointerDown
        );

        this.stage.addEventListener(
          "pointerup",
          this.onPointerUp
        );

        this.stage.addEventListener(
          "pointercancel",
          this.onPointerCancel
        );
      }
    }

    /* =========================================================
       NAVIGATION
       ========================================================= */

    handlePrevious() {
      this.goTo(
        this.currentIndex - 1
      );
    }

    handleNext() {
      this.goTo(
        this.currentIndex + 1
      );
    }

    normalizeIndex(index) {
      const total =
        this.slides.length;

      if (!total) {
        return 0;
      }

      return (
        (index % total) +
        total
      ) % total;
    }

    goTo(index) {
      if (
        this.transitioning ||
        this.slides.length <= 1
      ) {
        return;
      }

      const nextIndex =
        this.normalizeIndex(index);

      if (
        nextIndex ===
        this.currentIndex
      ) {
        return;
      }

      if (this.reducedMotion) {
        this.currentIndex =
          nextIndex;

        this.updateSlides({
          immediate: true
        });

        this.preloadAdjacentPosters();

        return;
      }

      this.transitioning = true;

      this.stage?.classList.add(
        "is-changing"
      );

      if (this.transitionTimer) {
        window.clearTimeout(
          this.transitionTimer
        );
      }

      this.transitionTimer =
        window.setTimeout(
          () => {
            if (this.destroyed) {
              return;
            }

            this.currentIndex =
              nextIndex;

            this.updateSlides({
              immediate: false
            });

            nextFrame(() => {
              this.stage?.classList.remove(
                "is-changing"
              );

              this.transitioning =
                false;

              this.preloadAdjacentPosters();
            });
          },
          280
        );
    }

    updateSlides({
      immediate = false
    } = {}) {
      this.slides.forEach(
        (slide, index) => {
          const active =
            index ===
            this.currentIndex;

          slide.classList.toggle(
            "is-active",
            active
          );

          slide.setAttribute(
            "aria-hidden",
            active ? "false" : "true"
          );

          const focusables =
            getFocusableElements(slide);

          focusables.forEach(
            (element) => {
              if (active) {
                if (
                  element.dataset
                    .dgtsOriginalTabindex !==
                  undefined
                ) {
                  const original =
                    element.dataset
                      .dgtsOriginalTabindex;

                  if (
                    original === ""
                  ) {
                    element.removeAttribute(
                      "tabindex"
                    );
                  } else {
                    element.setAttribute(
                      "tabindex",
                      original
                    );
                  }

                  delete element.dataset
                    .dgtsOriginalTabindex;
                }
              } else {
                if (
                  element.dataset
                    .dgtsOriginalTabindex ===
                  undefined
                ) {
                  element.dataset
                    .dgtsOriginalTabindex =
                    element.getAttribute(
                      "tabindex"
                    ) || "";
                }

                element.setAttribute(
                  "tabindex",
                  "-1"
                );
              }
            }
          );
        }
      );

      /*
       * The counter is repeated inside each slide.
       * Updating every instance guarantees 01 / 03
       * remains correct no matter which slide is active.
       */
      const counters =
        this.section.querySelectorAll(
          "[data-film-current]"
        );

      counters.forEach(
        (counter) => {
          counter.textContent =
            String(
              this.currentIndex + 1
            ).padStart(2, "0");
        }
      );

      if (immediate) {
        this.stage?.classList.remove(
          "is-changing"
        );
      }
    }

    /* =========================================================
       POSTER PRELOADING
       ========================================================= */

    preloadAdjacentPosters() {
      if (
        this.slides.length < 2
      ) {
        return;
      }

      const indexes = [
        this.normalizeIndex(
          this.currentIndex - 1
        ),
        this.normalizeIndex(
          this.currentIndex + 1
        )
      ];

      indexes.forEach(
        (index) => {
          const image =
            this.slides[
              index
            ]?.querySelector(
              "img"
            );

          if (
            !image ||
            !image.currentSrc
          ) {
            return;
          }

          const preload =
            new Image();

          preload.src =
            image.currentSrc;
        }
      );
    }

    /* =========================================================
       KEYBOARD NAVIGATION
       ========================================================= */

    handleSectionKeyDown(event) {
      if (
        this.modal &&
        !this.modal.hidden
      ) {
        return;
      }

      if (
        !this.section.contains(
          document.activeElement
        )
      ) {
        return;
      }

      if (
        event.key ===
        "ArrowLeft"
      ) {
        event.preventDefault();
        this.handlePrevious();
        return;
      }

      if (
        event.key ===
        "ArrowRight"
      ) {
        event.preventDefault();
        this.handleNext();
      }
    }

    /* =========================================================
       YOUTUBE URL PARSER
       ========================================================= */

    getYouTubeId(url) {
      if (!url) {
        return null;
      }

      const value =
        String(url).trim();

      if (!value) {
        return null;
      }

      /*
       * Accept a raw 11-character YouTube ID.
       */
      if (
        /^[a-zA-Z0-9_-]{11}$/.test(
          value
        )
      ) {
        return value;
      }

      try {
        const parsed =
          new URL(
            value,
            window.location.origin
          );

        const hostname =
          parsed.hostname
            .replace(/^www\./, "")
            .toLowerCase();

        let id = null;

        if (
          hostname ===
            "youtu.be"
        ) {
          id =
            parsed.pathname
              .split("/")
              .filter(Boolean)[0];
        }

        if (
          hostname ===
            "youtube.com" ||
          hostname ===
            "m.youtube.com" ||
          hostname ===
            "music.youtube.com" ||
          hostname ===
            "youtube-nocookie.com"
        ) {
          if (
            parsed.pathname ===
            "/watch"
          ) {
            id =
              parsed.searchParams.get(
                "v"
              );
          }

          if (
            !id &&
            parsed.pathname.startsWith(
              "/embed/"
            )
          ) {
            id =
              parsed.pathname
                .split("/")[2];
          }

          if (
            !id &&
            parsed.pathname.startsWith(
              "/shorts/"
            )
          ) {
            id =
              parsed.pathname
                .split("/")[2];
          }

          if (
            !id &&
            parsed.pathname.startsWith(
              "/live/"
            )
          ) {
            id =
              parsed.pathname
                .split("/")[2];
          }
        }

        if (
          id &&
          /^[a-zA-Z0-9_-]{11}$/.test(
            id
          )
        ) {
          return id;
        }
      } catch (error) {
        /*
         * Fall through to regex extraction.
         */
      }

      const match =
        value.match(
          /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/))([a-zA-Z0-9_-]{11})/
        );

      return match
        ? match[1]
        : null;
    }

    buildYouTubeUrl(id) {
      if (!id) {
        return null;
      }

      const params =
        new URLSearchParams({
          autoplay: "1",
          playsinline: "1",
          rel: "0",
          modestbranding: "1",
          enablejsapi: "1"
        });

      return (
        "https://www.youtube-nocookie.com/embed/" +
        encodeURIComponent(id) +
        "?" +
        params.toString()
      );
    }

    /* =========================================================
       OPEN FILM
       ========================================================= */

    handleOpen(event) {
      const button =
        event.currentTarget;

      const url =
        button.dataset.video || "";

      const youtubeId =
        this.getYouTubeId(url);

      if (!youtubeId) {
        return;
      }

      this.openModal(
        youtubeId,
        button
      );
    }

    openModal(
      youtubeId,
      sourceButton
    ) {
      if (
        !this.modal ||
        !this.player ||
        !youtubeId
      ) {
        return;
      }

      const embedUrl =
        this.buildYouTubeUrl(
          youtubeId
        );

      if (!embedUrl) {
        return;
      }

      this.previousFocus =
        sourceButton ||
        document.activeElement;

      /*
       * The iframe does not exist until this moment.
       */
      const iframe =
        document.createElement(
          "iframe"
        );

      iframe.src = embedUrl;

      iframe.title =
        sourceButton
          ?.getAttribute(
            "aria-label"
          ) ||
        "Team Stange featured film";

      iframe.allow = [
        "accelerometer",
        "autoplay",
        "clipboard-write",
        "encrypted-media",
        "gyroscope",
        "picture-in-picture",
        "web-share"
      ].join("; ");

      iframe.allowFullscreen = true;

      iframe.setAttribute(
        "referrerpolicy",
        "strict-origin-when-cross-origin"
      );

      this.destroyPlayer();

      this.player.appendChild(
        iframe
      );

      this.savedOverflow =
        document.documentElement.style
          .overflow;

      this.modal.hidden = false;

      document.body.classList.add(
        BODY_MODAL_CLASS
      );

      document.documentElement.style
        .overflow = "hidden";

      document.addEventListener(
        "keydown",
        this.onModalKeyDown
      );

      nextFrame(() => {
        const focusables =
          getFocusableElements(
            this.modal
          );

        const close =
          focusables.find(
            (element) =>
              element.matches(
                "[data-film-close]"
              )
          );

        (
          close ||
          focusables[0]
        )?.focus({
          preventScroll: true
        });
      });
    }

    /* =========================================================
       CLOSE FILM
       ========================================================= */

    closeModal() {
      if (
        !this.modal ||
        this.modal.hidden
      ) {
        return;
      }

      this.modal.hidden = true;

      /*
       * Removing the iframe is intentional.
       * This guarantees YouTube playback stops
       * immediately and avoids background audio.
       */
      this.destroyPlayer();

      document.body.classList.remove(
        BODY_MODAL_CLASS
      );

      document.removeEventListener(
        "keydown",
        this.onModalKeyDown
      );

      if (
        !document.body.classList.contains(
          BODY_INTRO_CLASS
        )
      ) {
        document.documentElement.style
          .overflow =
          this.savedOverflow || "";
      }

      if (
        this.previousFocus &&
        typeof this.previousFocus.focus ===
          "function"
      ) {
        this.previousFocus.focus({
          preventScroll: true
        });
      }

      this.previousFocus = null;
    }

    destroyPlayer() {
      if (!this.player) {
        return;
      }

      while (
        this.player.firstChild
      ) {
        this.player.removeChild(
          this.player.firstChild
        );
      }
    }

    /* =========================================================
       MODAL KEYBOARD + FOCUS TRAP
       ========================================================= */

    handleModalKeyDown(event) {
      if (
        !this.modal ||
        this.modal.hidden
      ) {
        return;
      }

      if (
        event.key ===
        "Escape"
      ) {
        event.preventDefault();
        this.closeModal();
        return;
      }

      if (
        event.key !== "Tab"
      ) {
        return;
      }

      const focusables =
        getFocusableElements(
          this.modal
        );

      if (!focusables.length) {
        event.preventDefault();
        return;
      }

      const first =
        focusables[0];

      const last =
        focusables[
          focusables.length - 1
        ];

      if (
        event.shiftKey &&
        document.activeElement ===
          first
      ) {
        event.preventDefault();

        last.focus();
        return;
      }

      if (
        !event.shiftKey &&
        document.activeElement ===
          last
      ) {
        event.preventDefault();

        first.focus();
      }
    }

    /* =========================================================
       DESTROY
       ========================================================= */

    destroy() {
      this.destroyed = true;

      if (
        this.transitionTimer
      ) {
        window.clearTimeout(
          this.transitionTimer
        );

        this.transitionTimer = null;
      }

      this.previousButtons.forEach(
        (button) => {
          button.removeEventListener(
            "click",
            this.onPrevious
          );
        }
      );

      this.nextButtons.forEach(
        (button) => {
          button.removeEventListener(
            "click",
            this.onNext
          );
        }
      );

      this.openButtons.forEach(
        (button) => {
          button.removeEventListener(
            "click",
            this.onOpen
          );
        }
      );

      this.closeButtons.forEach(
        (button) => {
          button.removeEventListener(
            "click",
            this.onClose
          );
        }
      );

      this.section.removeEventListener(
        "keydown",
        this.onSectionKeyDown
      );

this.stage?.removeEventListener(
        "pointerup",
        this.onPointerUp
      );

document.removeEventListener(
        "keydown",
        this.onModalKeyDown
      );

      if (
        this.modal &&
        !this.modal.hidden
      ) {
        this.closeModal();
      }

      this.destroyPlayer();
    }
  }


  /* =========================================================
     FEATURED FILM REGISTRY
     ========================================================= */

  const filmInstances =
    new WeakMap();

  function initFilmSection(
    section
  ) {
    if (!section) {
      return null;
    }

    const existing =
      filmInstances.get(section);

    if (existing) {
      return existing;
    }

    const reveal =
      new DGTeamStangeReveal(
        section
      );

    const film =
      new DGTeamStangeFilm(
        section
      );

    const instance = {
      reveal,
      film,

      destroy() {
        reveal.destroy();
        film.destroy();
      }
    };

    filmInstances.set(
      section,
      instance
    );

    return instance;
  }

  function destroyFilmSection(
    section
  ) {
    if (!section) {
      return;
    }

    const instance =
      filmInstances.get(
        section
      );

    if (!instance) {
      return;
    }

    instance.destroy();

    filmInstances.delete(
      section
    );
  }

  function initFilmSections(
    root = document
  ) {
    const selector =
      "[data-dg-team-stange-film]";

    const sections = [];

    if (
      root.matches &&
      root.matches(selector)
    ) {
      sections.push(root);
    }

    if (
      root.querySelectorAll
    ) {
      root
        .querySelectorAll(
          selector
        )
        .forEach(
          (section) => {
            sections.push(
              section
            );
          }
        );
    }

    sections.forEach(
      (section) => {
        initFilmSection(
          section
        );
      }
    );
  }


  /* =========================================================
     INITIALIZE FILMS
     ========================================================= */

  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        initFilmSections(
          document
        );
      },
      {
        once: true
      }
    );
  } else {
    initFilmSections(
      document
    );
  }


  /* =========================================================
     SHOPIFY — FILM SECTION LOAD
     ========================================================= */

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initFilmSections(
        event.target
      );
    }
  );


  /* =========================================================
     SHOPIFY — FILM SECTION UNLOAD
     ========================================================= */

  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      const selector =
        "[data-dg-team-stange-film]";

      const section =
        event.target.matches?.(
          selector
        )
          ? event.target
          : event.target
              .querySelector?.(
                selector
              );

      destroyFilmSection(
        section
      );
    }
  );


  /* =========================================================
     SHOPIFY BLOCK SELECTION
     Makes Theme Editor editing much easier.
     ========================================================= */

  document.addEventListener(
    "shopify:block:select",
    (event) => {
      const filmSection =
        event.target.closest?.(
          "[data-dg-team-stange-film]"
        );

      if (filmSection) {
        const instance =
          filmInstances.get(
            filmSection
          );

        const slide =
          event.target.closest?.(
            "[data-film-slide]"
          );

        if (
          instance?.film &&
          slide
        ) {
          const index =
            Number(
              slide.dataset
                .filmIndex
            );

          if (
            Number.isFinite(index)
          ) {
            instance.film.goTo(
              index - 1
            );
          }
        }
      }

      const machineSection =
        event.target.closest?.(
          "[data-dg-team-stange-machine]"
        );

      if (machineSection) {
        const instance =
          componentInstances.get(
            machineSection
          );

        const annotation =
          event.target.closest?.(
            "[data-dgts-annotation]"
          );

        if (
          instance?.component &&
          annotation
        ) {
          const index =
            Number(
              annotation.dataset
                .annotationIndex
            );

          if (
            Number.isFinite(index)
          ) {
            instance.component
              .setActive(
                index,
                {
                  focus: false
                }
              );
          }
        }
      }
    }
  );


  /* =========================================================
     GLOBAL ESCAPE SAFETY
     Ensures theme-editor refreshes cannot leave
     the document accidentally scroll-locked.
     ========================================================= */

  document.addEventListener(
    "shopify:section:reorder",
    () => {
      if (
        !document.querySelector(
          ".dg-team-stange-gallery__lightbox:not([hidden]), .dg-team-stange-film__modal:not([hidden])"
        ) &&
        !document.querySelector(
          ".dg-team-stange-hero.is-intro-active"
        )
      ) {
        document.body.classList.remove(
          BODY_MODAL_CLASS,
          BODY_INTRO_CLASS
        );

        document.documentElement.style
          .overflow = "";
      }
    }
  );

})();

/* =========================================================
   TEAM STANGE — INTRO CONTROL PORTAL
   Keeps sound + skip controls above every video/section layer.
   ========================================================= */

(() => {
  'use strict';

  const initIntroControls = (hero) => {
    if (!hero || hero.dataset.dgIntroControlsPortal === 'true') {
      return;
    }

    const intro = hero.querySelector('[data-dgts-intro]');

    if (!intro) {
      return;
    }

    /*
     * Supports BOTH versions we've worked with:
     *
     * New:
     * .dg-team-stange-hero__intro-controls
     *
     * Existing/old:
     * .dg-team-stange-hero__intro-actions
     */
    const controls =
      hero.querySelector('.dg-team-stange-hero__intro-controls') ||
      hero.querySelector('.dg-team-stange-hero__intro-actions');

    if (!controls) {
      return;
    }

    hero.dataset.dgIntroControlsPortal = 'true';

    /*
     * Save original position so Shopify section editing/
     * cleanup remains safe.
     */
    const placeholder = document.createComment(
      'DG Team Stange intro controls'
    );

    controls.parentNode.insertBefore(
      placeholder,
      controls
    );

    /*
     * Move the REAL buttons to <body>.
     * Event listeners stay attached because we're moving,
     * not cloning.
     */
    document.body.appendChild(controls);

    controls.classList.add(
      'dg-team-stange-hero__intro-controls--portal'
    );

    /*
     * Ensure the controls are physically clickable.
     */
    Object.assign(controls.style, {
      position: 'fixed',
      top: '32px',
      right: '48px',
      left: 'auto',
      bottom: 'auto',
      zIndex: '2147483647',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: '28px',
      width: 'auto',
      height: '56px',
      margin: '0',
      padding: '0',
      transform: 'none',
      opacity: '1',
      visibility: 'visible',
      pointerEvents: 'auto'
    });

    const buttons = controls.querySelectorAll(
      '.dg-team-stange-hero__sound, .dg-team-stange-hero__skip'
    );

    buttons.forEach((button) => {
      Object.assign(button.style, {
        position: 'relative',
        zIndex: '2',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '48px',
        padding: '0 12px',
        cursor: 'pointer',
        pointerEvents: 'auto'
      });

      button
        .querySelectorAll('*')
        .forEach((child) => {
          child.style.pointerEvents = 'none';
        });
    });

    /*
     * Show controls only while intro is active.
     */
    const syncVisibility = () => {
      const active =
        hero.classList.contains('is-intro-active') &&
        !hero.classList.contains(
          'is-intro-transitioning'
        );

      controls.style.opacity =
        active ? '1' : '0';

      controls.style.visibility =
        active ? 'visible' : 'hidden';

      controls.style.pointerEvents =
        active ? 'auto' : 'none';

      buttons.forEach((button) => {
        button.style.pointerEvents =
          active ? 'auto' : 'none';
      });
    };

    const observer = new MutationObserver(
      syncVisibility
    );

    observer.observe(hero, {
      attributes: true,
      attributeFilter: ['class']
    });

    syncVisibility();

    /*
     * Shopify Theme Editor cleanup.
     */
    hero._dgIntroControlsCleanup = () => {
      observer.disconnect();

      if (
        placeholder.parentNode &&
        controls.parentNode
      ) {
        placeholder.parentNode.insertBefore(
          controls,
          placeholder
        );

        placeholder.remove();
      }

      controls.classList.remove(
        'dg-team-stange-hero__intro-controls--portal'
      );

      controls.removeAttribute('style');

      buttons.forEach((button) => {
        button.removeAttribute('style');

        button
          .querySelectorAll('[style]')
          .forEach((child) => {
            child.style.removeProperty(
              'pointer-events'
            );

            if (!child.getAttribute('style')) {
              child.removeAttribute('style');
            }
          });
      });

      delete hero.dataset.dgIntroControlsPortal;
    };
  };


  const boot = (root = document) => {
    const heroes = [];

    if (
      root.matches &&
      root.matches('[data-dg-team-stange-hero]')
    ) {
      heroes.push(root);
    }

    if (root.querySelectorAll) {
      root
        .querySelectorAll(
          '[data-dg-team-stange-hero]'
        )
        .forEach((hero) => {
          heroes.push(hero);
        });
    }

    heroes.forEach(initIntroControls);
  };


  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => boot(document),
      { once: true }
    );
  } else {
    boot(document);
  }


  document.addEventListener(
    'shopify:section:load',
    (event) => {
      boot(event.target);
    }
  );


  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const hero =
        event.target.matches?.(
          '[data-dg-team-stange-hero]'
        )
          ? event.target
          : event.target.querySelector?.(
              '[data-dg-team-stange-hero]'
            );

      if (
        hero &&
        typeof hero._dgIntroControlsCleanup ===
          'function'
      ) {
        hero._dgIntroControlsCleanup();
      }
    }
  );
})();
